// static/js/sidebar_layout.js
//
// The desktop sidebar's collapse toggle and drag-to-resize handle (ADR-0068).
// The state is written to one preference cookie, which the server reads to
// render the next page at the same width (app/utils/sidebar_layout.py); this
// module only changes the live page and the cookie. Below the `md` breakpoint
// the sidebar is Bootstrap's off-canvas drawer, and the stylesheet ignores
// both the width and the collapsed class there.

const COOKIE_NAME = 'sidebar_layout';
const COOKIE_MAX_AGE_S = 365 * 24 * 60 * 60;
const DEFAULT_WIDTH = 280;
const KEY_STEP = 16;

function readState(layout, resizer) {
    return {
        width: Number(resizer.getAttribute('aria-valuenow')) || DEFAULT_WIDTH,
        min: Number(resizer.getAttribute('aria-valuemin')),
        max: Number(resizer.getAttribute('aria-valuemax')),
        collapsed: layout.classList.contains('sidebar-collapsed'),
    };
}

function saveState(state) {
    const value = `${Math.round(state.width)}-${state.collapsed ? 1 : 0}`;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${value}; Path=/; Max-Age=${COOKIE_MAX_AGE_S}; SameSite=Lax${secure}`;
}

// Charts and other width-dependent content re-measure on `resize`; a changed
// sidebar changes the main area's width without the window's.
function announceResize() {
    window.dispatchEvent(new Event('resize'));
}

function applyWidth(ctx, width) {
    const { state, layout, resizer } = ctx;
    state.width = Math.min(Math.max(width, state.min), state.max);
    layout.style.setProperty('--sidebar-width', `${state.width}px`);
    resizer.setAttribute('aria-valuenow', String(Math.round(state.width)));
}

function setCollapsed(ctx, collapsed) {
    const { state, layout, toggle } = ctx;
    state.collapsed = collapsed;
    layout.classList.toggle('sidebar-collapsed', collapsed);
    if (toggle) {
        toggle.setAttribute('aria-expanded', String(!collapsed));
    }
    saveState(state);
}

function bindToggle(ctx) {
    const { toggle, layout } = ctx;
    if (!toggle) {
        return;
    }
    toggle.addEventListener('click', () => setCollapsed(ctx, !ctx.state.collapsed));
    // The width transition is what moves the main area; re-measure after it.
    layout.addEventListener('transitionend', (event) => {
        if (event.target.id === 'appSidebar' && event.propertyName === 'width') {
            announceResize();
        }
    });
}

function bindDrag(ctx) {
    const { resizer, layout, sidebar } = ctx;
    resizer.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) {
            return;
        }
        // No preventDefault(): it suppresses the dblclick that resets the
        // width. Text selection is held off by `.is-resizing` instead.
        resizer.setPointerCapture(event.pointerId);
        layout.classList.add('is-resizing');
    });
    resizer.addEventListener('pointermove', (event) => {
        if (resizer.hasPointerCapture(event.pointerId)) {
            applyWidth(ctx, event.clientX - sidebar.getBoundingClientRect().left);
        }
    });
    const finish = (event) => endDrag(ctx, event);
    resizer.addEventListener('pointerup', finish);
    resizer.addEventListener('pointercancel', finish);
}

function endDrag(ctx, event) {
    const { resizer, layout, state } = ctx;
    if (!layout.classList.contains('is-resizing')) {
        return;
    }
    if (resizer.hasPointerCapture(event.pointerId)) {
        resizer.releasePointerCapture(event.pointerId);
    }
    layout.classList.remove('is-resizing');
    saveState(state);
    announceResize();
}

const KEY_WIDTHS = {
    ArrowLeft: (state) => state.width - KEY_STEP,
    ArrowRight: (state) => state.width + KEY_STEP,
    Home: (state) => state.min,
    End: (state) => state.max,
};

function bindKeysAndReset(ctx) {
    const { resizer, state } = ctx;
    resizer.addEventListener('keydown', (event) => {
        const next = KEY_WIDTHS[event.key];
        if (!next) {
            return;
        }
        event.preventDefault();
        applyWidth(ctx, next(state));
        saveState(state);
        announceResize();
    });
    resizer.addEventListener('dblclick', () => {
        applyWidth(ctx, DEFAULT_WIDTH);
        saveState(state);
        announceResize();
    });
}

export function initSidebarLayout() {
    const layout = document.querySelector('.app-layout');
    const resizer = document.querySelector('.app-sidebar-resizer');
    const sidebar = document.getElementById('appSidebar');
    if (!layout || !resizer || !sidebar) {
        return;
    }
    const toggle = document.getElementById('sidebarToggle');
    const ctx = { layout, resizer, sidebar, toggle, state: readState(layout, resizer) };
    bindToggle(ctx);
    bindDrag(ctx);
    bindKeysAndReset(ctx);
}
